import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogConfig as MatDialogConfig } from "@angular/material/legacy-dialog";
import { first } from "rxjs/operators";
import * as moment from "moment";
import { Subscription } from "rxjs";

import { AppGlobals } from "../../app.globals";
import { IUser } from "../../_models";
import { HousingService, AlertService, MediaWatcherService } from "../../_services";
import { UnSavedChangesDialogComponent } from "../../_components/unSavedChangesDialog/unSavedChangesDialog.component";

@Component({
    templateUrl: "checkoutDialog.component.html",
    styleUrls: ["checkoutDialog.component.css"]
})
export class CheckoutDialogComponent implements OnInit, OnDestroy {
    checkoutConfirmationFormGroup: UntypedFormGroup;
    checkoutFormGroup: UntypedFormGroup;
    allocationPeriod: string;
    currentUser: IUser;
    housingProgramId?: number;
    housingStudentApplicationId: number;
    moveOutDate: Date;
    moveInDate: Date;
    showCheckoutInformation = false;
    allowEdit = true;
    keyCode: string;
    displayIndividualBuildings: boolean;
    isRecurring: boolean;
    nextSemesterKeyCode: string;
    nextSemesterBuildingName: string;
    lateCheckOutChargeAmount: number;
    nonReturnedKeysChargeAmount: number;
    today = new Date();
    isMobile = false;
    isBeforeDeadline = false;
    mediaWatcherSubscription: Subscription;

    constructor(private dialogRef: MatDialogRef<CheckoutDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: any,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        private housingService: HousingService,
        private appGlobals: AppGlobals,
        private alertService: AlertService,
        private mediaWatcherService: MediaWatcherService) {
        this.currentUser = data.currentUser;
        this.allocationPeriod = data.allocationPeriod;
        this.housingProgramId = data.housingProgramId;
        this.housingStudentApplicationId = data.housingStudentApplicationId;
        this.moveOutDate = data.moveOutDate;
        this.moveInDate = data.moveInDate;
        this.allowEdit = data.allowEdit;
        this.keyCode = data.keyCode;
        this.displayIndividualBuildings = data.displayIndividualBuildings;
        this.isRecurring = data.isRecurring;
        this.nextSemesterKeyCode = data.nextSemesterKeyCode;
        this.nextSemesterBuildingName = data.nextSemesterBuildingName;
    }

    ngOnDestroy() {
        this.mediaWatcherSubscription.unsubscribe();
    }

    ngOnInit() {
        //this.googleAnalyticsService.logPageView("/housingCheckOut");

        this.mediaWatcherSubscription = this.mediaWatcherService.isTabletLarge.subscribe(isMobile => {
            this.isMobile = isMobile;
        });

        this.initializeForm();

        this.onChanges();

        this.housingService.getCheckoutCharges(this.currentUser.currencyId)
            .pipe(first())
            .subscribe((result: any) => {
                this.lateCheckOutChargeAmount = result.lateCheckOutChargeAmount;
                this.nonReturnedKeysChargeAmount = result.nonReturnedKeysChargeAmount;
            });
    }

    initializeForm() {
        this.checkoutConfirmationFormGroup = this.formBuilder.group({
            belongings: [{ value: "", disabled: false }, Validators.required],
            sheets: [{ value: "", disabled: false }, Validators.required],
            trash: [{ value: "", disabled: false }, Validators.required],
            keys: [{ value: "", disabled: false }, Validators.required],
            card: [{ value: "", disabled: false }],
            luggage: [{ value: "", disabled: false }, Validators.required]
        });

        this.checkoutFormGroup = this.formBuilder.group({
            departureDate: [{ value: "", disabled: !this.allowEdit }, Validators.required],
            departureTime: [{ value: "", disabled: !this.allowEdit }, Validators.required]
        });
    }

    onChanges() {
        this.checkoutFormGroup.get("departureDate").valueChanges
            .subscribe(departureDate => {
                if (departureDate)
                    this.isBeforeDeadline = new Date(departureDate) < new Date(this.moveOutDate);

                this.checkoutFormGroup.get("departureTime").reset("00:00 am");
                this.checkoutFormGroup.get("departureTime").reset("");
            });
    }

    get checkoutConfirmationFormControls() { return this.checkoutConfirmationFormGroup.controls; }

    get checkoutFormControls() { return this.checkoutFormGroup.controls as any; }

    saveCheckoutInformation(submitBtn: HTMLButtonElement) {
        if (this.checkoutFormGroup.invalid) {
            return;
        }

        if (this.checkoutFormGroup.dirty) {
            setTimeout(() => {
                submitBtn.disabled = true;
            }, 0);

            const saveModel = {
                housingStudentApplicationId: this.housingStudentApplicationId,
                departureDate: moment(this.checkoutFormControls.departureDate.value).format("YYYY-MM-DD"),
                departureTime: moment(this.checkoutFormControls.departureTime.value, "hh:mm A").format("HH:mm"),
                studentBbUniqueId: this.currentUser.bbUniqueId,
                scholasticPeriod: !this.housingProgramId ? this.allocationPeriod : null,
                moveInDate: this.moveInDate,
                moveOutDate: this.moveOutDate
            };

            this.housingService.insertCheckoutInformation(saveModel)
                .pipe(first())
                .subscribe(() => {
                    this.alertService.showMessage("Checkout Information Successfully Submitted.", "Housing Application", this.appGlobals.alertMessageTypes.success);

                    this.dialogRef.close(true);
                });
        }
    }

    close() {
        if (this.checkoutFormGroup.dirty) {
            //show unsaved changes dialog
            const dialogConfig = new MatDialogConfig();

            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            dialogConfig.closeOnNavigation = true;
            dialogConfig.width = this.isMobile ? "60vw" : "30vw";
            dialogConfig.height = "auto";
            dialogConfig.panelClass = "unSavedChanges-dialog-container";

            const dialogRef = this.dialog.open(UnSavedChangesDialogComponent, dialogConfig);

            dialogRef.afterClosed().subscribe(result => {
                if (result === "confirm") {
                    this.dialogRef.close();
                }
            });
        } else {
            this.dialogRef.close();
        }
    }
}